import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Save, CheckCircle2, Loader2, Plus, Trash2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';

const ASSET_TYPES = [
  ['shares', 'Shares'], ['etfs', 'ETFs'], ['managed_funds', 'Managed funds'], ['crypto', 'Crypto'],
  ['property_sale', 'Property sale'], ['business_asset_sale', 'Business asset sale'], ['other', 'Other CGT asset'],
];
const EMPTY = {
  asset_type: 'shares', platform_or_broker: '', purchase_date: '', sale_date: '',
  cost_base: '', sale_proceeds: '', brokerage_fees: '', ownership_percentage: 100,
};

export default function InvestmentsForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  useEffect(() => {
    if (!clientId) return;
    onboardingAPI.getSection(clientId, 'investments')
      .then((res) => setItems(res.data.response_data?.investments || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  const persist = async (nextItems, status) => {
    await onboardingAPI.saveSection(clientId, 'investments', {
      response_data: { investments: nextItems }, status,
    });
  };

  const addItem = () => {
    if (!draft.platform_or_broker.trim()) { toast.error('Platform / broker name is required'); return; }
    const next = [...items, draft];
    setItems(next); setDraft(EMPTY); setShowForm(false);
    persist(next, 'in_progress').catch(() => toast.error('Failed to save'));
  };
  const removeItem = (i) => { const next = items.filter((_, idx) => idx !== i); setItems(next); persist(next, 'in_progress'); };

  const finalize = async (status) => {
    setSaving(true);
    try { await persist(items, status); toast.success(status === 'completed' ? 'Investments saved' : 'Saved'); navigate('/onboarding'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  const label = (v) => (ASSET_TYPES.find((a) => a[0] === v) || [])[1] || v;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Investments &amp; Capital Gains</CardTitle>
          <CardDescription>Add shares, managed funds, crypto or asset sales relevant to capital gains tax.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-3 border rounded-lg p-3">
              <TrendingUp className="w-5 h-5 text-foreground" />
              <div className="flex-1">
                <div className="font-medium text-foreground">{label(it.asset_type)} — {it.platform_or_broker}</div>
                <div className="text-xs text-muted-foreground">cost base {it.cost_base || '—'} · proceeds {it.sale_proceeds || '—'} · {it.ownership_percentage}%</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => removeItem(i)} title="Remove"><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}

          {showForm ? (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Asset type *</Label>
                  <Select value={draft.asset_type} onValueChange={set('asset_type')}>
                    <SelectTrigger data-testid="inv-asset-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{ASSET_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Platform / broker name *</Label><Input value={draft.platform_or_broker} onChange={onInput('platform_or_broker')} data-testid="inv-platform" /></div>
                <div className="space-y-2"><Label>Purchase date</Label><Input type="date" value={draft.purchase_date} onChange={onInput('purchase_date')} /></div>
                <div className="space-y-2"><Label>Sale date</Label><Input type="date" value={draft.sale_date} onChange={onInput('sale_date')} /></div>
                <div className="space-y-2"><Label>Cost base</Label><Input type="number" value={draft.cost_base} onChange={onInput('cost_base')} /></div>
                <div className="space-y-2"><Label>Sale proceeds</Label><Input type="number" value={draft.sale_proceeds} onChange={onInput('sale_proceeds')} /></div>
                <div className="space-y-2"><Label>Brokerage / fees</Label><Input type="number" value={draft.brokerage_fees} onChange={onInput('brokerage_fees')} /></div>
                <div className="space-y-2"><Label>Ownership %</Label><Input type="number" value={draft.ownership_percentage} onChange={onInput('ownership_percentage')} /></div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setShowForm(false); setDraft(EMPTY); }}>Cancel</Button>
                <Button onClick={addItem} data-testid="inv-add-submit"><Plus className="w-4 h-4 mr-1" /> Add investment</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setShowForm(true)} data-testid="inv-add-btn"><Plus className="w-4 h-4 mr-2" /> Add an investment / asset</Button>
          )}

          <p className="text-[11px] text-muted-foreground/70">Supporting documents (broker statements, contract notes, crypto reports) will be available in the Document Centre.</p>

          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => finalize('in_progress')} disabled={saving}><Save className="w-4 h-4 mr-2" /> Save &amp; return later</Button>
            <Button onClick={() => finalize('completed')} disabled={saving} data-testid="inv-complete"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
